import { capitalize, fixUTF } from '@jotforminc/utils';
import { unsupportedDecimalCurrencies } from '@jotforminc/payment-constants';
import { TIMESTAMP_OF_2019 } from './constants';

if (global.Element && !global.Element.prototype.matches) {
  global.Element.prototype.matches = global.Element.prototype.matchesSelector
      || global.Element.prototype.mozMatchesSelector
      || global.Element.prototype.msMatchesSelector
      || global.Element.prototype.oMatchesSelector
      || global.Element.prototype.webkitMatchesSelector
      || function matchesFN(s) {
        var matches = (this.document || this.ownerDocument).querySelectorAll(s);
        var i = matches.length;
        while (--i >= 0 && matches.item(i) !== this) {
          // eslist-disable-next-line
        }
        return i > -1;
      };
}

export function htmlDecode(text, quoteS) {
  let quoteStyle = quoteS;
  let string = text ?? '';
  let optTemp = 0;
  let i = 0;
  let noquotes = false;
  if (typeof quoteStyle === 'undefined') {
    quoteStyle = 2;
  }
  string = string.toString().replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  const OPTS = {
    ENT_NOQUOTES: 0,
    ENT_HTML_QUOTE_SINGLE: 1,
    ENT_HTML_QUOTE_DOUBLE: 2,
    ENT_COMPAT: 2,
    ENT_QUOTES: 3,
    ENT_IGNORE: 4
  };
  if (quoteStyle === 0) {
    noquotes = true;
  }
  if (typeof quoteStyle !== 'number') {
    quoteStyle = [].concat(quoteStyle);
    for (i = 0; i < quoteStyle.length; i++) {
      if (OPTS[quoteStyle[i]] === 0) {
        noquotes = true;
      } else if (OPTS[quoteStyle[i]]) {
        optTemp = optTemp || OPTS[quoteStyle[i]];
      }
    }
    quoteStyle = optTemp;
  }
  if (quoteStyle && OPTS.ENT_HTML_QUOTE_SINGLE) {
    string = string.replace(/&#0*39;/g, "'").replace(/&#x27;/g, "'");
  }
  if (!noquotes) {
    string = string.replace(/&quot;/g, '"');
    string = string.replace(/&amp;quot;/g, '"');
  }

  string = string.replace(/&amp;/g, '&');

  return string;
}

export const isOldBlotId = id => Number(id) > TIMESTAMP_OF_2019;
export const setParsedId = ([x, y]) => (isOldBlotId(x) ? ({ id: x, label: y }) : ({ id: y, label: x }));
export const parseBlotId = (blotId = '') => setParsedId(blotId.split('-'));
export const setBlotId = id => label => (isOldBlotId(id) ? `${id}-${label}` : `${label}-${id}`);

export const getBlotOptions = (field) => {
  if (field.type === 'control_dropdown') {
    return [field.options.reduce((acc, o) => ({
      type: o.type,
      id: setBlotId(field.id)(o.type),
      default: field.label,
      options: [...(acc?.options || [acc.label]), o.label]
    }))];
  }

  return field.options || [];
};

export function debounce(func, wait, immediate) {
  let timeout;
  return function() {
    const context = this, args = arguments;
    const later = function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export const classNames = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    if (!obj[key]) return acc;
    return [...acc, key];
  }, []).join(' ');
};

export const assignObject = (...objects) => {
  return objects.reduce((acc, obj) => {
    Object.keys(obj).forEach((key) => {
      acc[key] = obj[key];
    });

    return acc;
  }, {});
};

export const fillArray = (arr, value) => {
  const newArr = [];
  for (let i = 0; i < arr.length; i++) {
    newArr.push(value);
  }
  return newArr;
};

export const getJSON = (url) => {
  if (typeof Ajax !== 'undefined') {
    return new Promise((resolve) => {
      new Ajax.Request(url, {
        evalJSON: 'force',
        method: 'GET',
        onComplete: response => resolve(response.responseJSON)
      });
    })
  }
  return fetch(url).then(res => res.json());
};

export function getCDN() {
  return '/';
}

export function getCurrentTime(prop, currentHour, currentMin, currentHourRange, currentMinRange) {
  const date = new Date();

  let hour = !currentHour ? date.getHours() : currentHour;
  let min = !currentMin ? date.getMinutes() : currentMin;
  let hourRange = !currentHourRange ? hour : currentHourRange;
  let minRange = !currentMin ? min : currentMinRange;

  const step = (prop.step.value) ? prop.step.value : 10;

  const getTime = function (timeHour, timeMinute) {
    let currentAmpm = '';

    let h = Number(timeHour);
    let m = Number(timeMinute);

    let twentyFour = true;
    if (prop.timeFormat.value === 'AM/PM') {
      twentyFour = false;
      currentAmpm = (h > 11) ? 'PM' : 'AM';
      h = (h > 12) ? h - 12 : h;
      h = (h === 0) ? 12 : h;
    }

    m = Math.round(m / step) * step;
    m = addZeros(m, 2);
    if (m >= 60) { // ntw roll over to next h/day
      m = '00';
      h++;
      if (twentyFour) {
        if (h === 24) h = 0;
      } else if (currentAmpm === 'AM' && h === 12) currentAmpm = 'PM';
      else if (currentAmpm === 'PM' && h === 12) currentAmpm = 'AM';
      else if (h === 13) h = 1;
    }
    return { hour: h.toString(), min: m, currentAmpm };
  };

  const fromD = getTime(hour, min);
  hour = prop.timeFormat.value === 'AM/PM' ? fromD.hour : addZeros(fromD.hour, 2);
  min = fromD.min;
  const currentAmpm = fromD.currentAmpm;

  const to = getTime(hourRange, minRange);
  hourRange = to.hour;
  minRange = to.min;
  const currentAmpmRange = to.currentAmpm;

  const time = {
    limit: prop.timeFormat.value === 'AM/PM' ? 12 : 23,
    start: prop.timeFormat.value === 'AM/PM' ? 1 : 0,
    step,

    hour,
    min,
    currentAmpm,

    hourRange,
    minRange,
    currentAmpmRange
  };

  return time;
}

export function getDefaultTime(prop) {
  if (!prop.defaultTime.value || prop.defaultTime.value === 'none') {
    return '';
  }
  if (prop.defaultTime.value.indexOf('custom_') > -1) {
    let toCustomHour = false;
    let toCustomMinute = false;
    let fromD = prop.defaultTime.value.split('custom_')[1];
    // time range
    if (fromD.indexOf('-') > -1) {
      const range = fromD.split('-');
      const to = range[1];
      fromD = range[0];
      const toSplit = to.split(':');
      toCustomHour = toSplit[0];
      toCustomMinute = toSplit[1];
    }

    return getCurrentTime(prop, fromD.split(':')[0], fromD.split(':')[1], toCustomHour, toCustomMinute);
  }

  return getCurrentTime(prop);
}

export function newlineToEntities(string) {
  return string.toString().replace(/\n\r?/g, '&#13;&#10;');
}


/**
* Build parameters and settings for the widget
*/
export function buildParams(_params, fieldNames, prop) {
  for (let i = 0; i < fieldNames.length; i++) {
    const key = fieldNames[i];
    if (key === '') { continue; }
    const el = prop[key];
    if (el && el.value) {
      let propvalue = (typeof el.value === 'string') ? htmlDecode(el.value) : el.value;

      // from here lets check chunks - lets include them
      if ('paramChunks' in prop && prop.paramChunks.value) {
        const chunkProps = prop.paramChunks.value;
        /* eslint guard-for-in:0 */
        for (const chunkKey in chunkProps) {
          const chunkProp = chunkProps[chunkKey];
          // if current key matches the mainpart
          // it means we have a chunks to merge
          if (chunkKey === key && chunkProp.parts) {
            for (let y = 0; y < chunkProp.parts.length; y++) {
              const partnameProp = chunkProp.parts[y];
              // if part name is registered as prop then concatenate to final value
              if (prop[partnameProp] && prop[partnameProp].value) {
                propvalue += prop[partnameProp].value;
              }
            }
          }
        }
        /* eslint guard-for-in:1 */
      }

      // push the data to settings array
      _params.push({
        name: key,
        value: propvalue
      });

      // avoid memory leak
      propvalue = null;
    }
  }
  return _params;
}
export function fixNumbers(number) {
  if (typeof number === 'string') {
    return number.replace(/\D/gim, '');
  }
  return number;
}

export function numberFormat(number, decimals, decPoint, thousandsSep) {
  let n = number;
  let prec = decimals;

  const toFixedFix = function (num, dec) {
    const k = Math.pow(10, dec);
    return (Math.round(num * k) / k).toString();
  };

  n = !isFinite(+n) ? 0 : +n;
  prec = !isFinite(+prec) ? 0 : Math.abs(prec);
  const sep = (typeof thousandsSep === 'undefined') ? ',' : thousandsSep;
  const dec = (typeof decPoint === 'undefined') ? '.' : decPoint;
  let s = (prec > 0) ? toFixedFix(n, prec) : toFixedFix(Math.round(n), prec);
  const abs = toFixedFix(Math.abs(n), prec);
  let _;
  let i;

  if (abs >= 1000) {
    _ = abs.split(/\D/);
    i = _[0].length % 3 || 3;
    _[0] = s.slice(0, i + (n < 0)) + _[0].slice(i).replace(/(\d{3})/g, `${sep}$1`);
    s = _.join(dec);
  } else {
    s = s.replace('.', dec);
  }

  if (s.indexOf(dec) === -1 && prec > 1) {
    const preca = [];
    preca[prec - 1] = undefined;
    s += `${dec + preca.join(0)}0`;
  } else if (s.indexOf(dec) === s.length - 2 && s.length > 1) { // incorrect: 2.7, correct: 2.70
    s += '0';
  }

  return s;
}


export function formatPrice(priceAmount, currency, priceID, nofree, decimalMark) {
  const curr = !currency ? 'USD' : currency;

  const id = priceID || '';
  let amount = priceAmount;
  // decimal places
  const dec = unsupportedDecimalCurrencies.indexOf(curr) > -1 ? 0 : 2;

  if (parseFloat(amount) === 0 && nofree !== true) {
    amount = 'Free'.locale();
  } else {
    const useComma = decimalMark && decimalMark === 'comma';
    amount = numberFormat(amount, dec, (useComma ? ',' : '.'), (useComma ? '.' : ',')); // Format the number for money currency
  }
  let symbol;
  switch (curr) {
    case 'EUR': symbol = ['&euro;', curr]; break;
    case 'GBP': symbol = ['&pound;', curr]; break;
    case 'BRL': symbol = ['R$', '']; break;
    case 'JPY': symbol = ['¥', curr]; break;
    case 'USD': symbol = ['$', '']; break;
    case 'AUD':
    case 'CAD':
    case 'NZD':
    case 'SGD':
    case 'HKD':
    case 'CLP':
      symbol = ['$', curr]; break;
    case 'TRY':
      symbol = ['', 'TL']; break;
    default:
      symbol = ['', curr]; break;
  }

  let out;
  // the word 'free' can be in other languages
  // parse and check if it's a number to determine if it's free or not
  if (isNaN(parseInt(amount, 10))) {
    out = `<span class='freeCurr' style='display:none;'>&nbsp${symbol[0]}</span><span id="${id}">&nbsp${amount}</span> <span class='freeCurr' style='display:none;'>&nbsp${symbol[1]}</span>`;
  } else {
    out = `${symbol[0]}<span id="${id}">${amount}</span> ${symbol[1]}`;
  }
  return out;
}
/**
 * Makes formatted text for products. Just like paypal
 * @param {Object} name
 * @param {Object} price
 * @param {Object} curr
 * @param {Object} duration
 * @param {Object} setupfee
 * @param {Object} trial
 * @param {Object} id
 * @param {Object} times (number of subscription payments)
 * @param {Boolean} noPrice
 * @param {Boolean} passive Whether env is form builder or not
 * @param {Boolean} customPrice Whether the item's price is variable/user-defined (for subscriptions only)
 */
export function makeProductText(name, price, curr, duration, setupfee, productTrial, id, times, noPrice, passive, customPrice, customPriceSource) {
  let text = '';
  let currDisplay = curr ? curr : undefined;

  switch (curr) {

    case 'TRY':
      currDisplay = 'TL';
      break;

      // case 'USD':
      //     currDisplay = '$';
      // break;

    default:
      currDisplay = curr;
      break;

  }

  let fprice = noPrice ? '' : `<b>${formatPrice(price || 0, currDisplay, id || '')}</b>`; // Get formatted price
  let fsetupfee = `<b>${formatPrice(Number(setupfee) || 0, curr, id ? id.replace('price', 'setupfee') : '')}</b>`; // Get calculated and formatted setupfee
  let setuptext = ''; // setupfee text will be here
  const trialText = setupfee > 0 ? fsetupfee : 'Free'.locale();
  const installmentText = '.'; // installmentText placeholder


  if (customPrice === '1') {
    const readOnly = customPriceSource > 0 || passive;

    fprice = `<b><input value="${price}" ${id ? (`id="${id.replace('_price', '')
               }_custom_price" `) : ''}class="form-product-custom_price" type="text" name=""${
               readOnly ? ' readonly' : ''}`;

    if (customPriceSource > 0) {
      fprice += ` data-price-source="${customPriceSource}"`;
    }

    fprice += `/>${currDisplay}</b>`;
  }

  /** re-implement if requested
   *
  if(times && !isNaN(times)) {
      installmentText = times !== '1' ? ", for " + times + " installments." : ".";
  }
  **/

  let trial;
  if (duration && productTrial && productTrial.toLowerCase() !== 'none' && productTrial !== 'Enabled') { // If trial period is set
    trial = productTrial.locale();
    if (trial === 'One Day') { // Special text for 1 day trial
      text += `${trialText} ${'for the first day then,'.locale()} `;
    } else {
      text += `${trialText} ${'for the first <u>'.locale()}${trial.toLowerCase()}${'</u> then,'.locale()} `;
    }
  }

  if (trial === 'Enabled') {
    fsetupfee = 'Free'.locale();
  }

  setuptext = `${fsetupfee} ${'for the <u>first payment</u> then,'.locale()} `;

  if (duration) { // If recurring duration is set
      // If recurring times is equal to 1
    if (times === '1') {
      text += `${fprice} ${'one-time payment'.locale()}`;
    } else {
      switch (duration) {
        case 'Daily':
          setuptext = `${fsetupfee} ${'for the <u>first day</u> then,'.locale()} `;
          text += `${fprice} ${'for each <u>day</u>'.locale()}`;
          break;
        case 'Weekly':
          setuptext = `${fsetupfee} ${'for the <u>first week</u> then,'.locale()} `;
          text += `${fprice} ${'for each <u>week</u>'.locale()}`;
          break;
        case 'Bi-Weekly':
          text += `${fprice} ${'for each <u>two weeks</u>'.locale()}`;
          break;
        case 'Monthly':
          setuptext = `${fsetupfee} ${'for the <u>first month</u> then,'.locale()} `;
          text += `${fprice} ${'for each <u>month</u>'.locale()}`;
          break;
        case 'Bi-Monthly':
          text += `${fprice} ${'for each <u>two months</u>'.locale()}`;
          break;
        case 'Quarterly':
          text += `${fprice} ${'for each <u>three months</u>'.locale()}`;
          break;
        case 'Semi-Yearly':
          text += `${fprice} ${'for each <u>six months</u>'.locale()}`;
          break;
        case 'Yearly':
          setuptext = `${fsetupfee} for the <u>first year</u> then, `;
          text += `${fprice} ${'for each <u>year</u>'.locale()}`;
          break;
        case 'Bi-Yearly':
          text += `${fprice} ${'for each <u>two years</u>'.locale()}`;
          break;
        default:
          if (duration.match(/custom-/g)) {
            setuptext = `${fsetupfee} for the <u>first ${duration.replace(/custom-/g, '')} days</u> then,`;
            text += `${fprice} every <u> ${duration.replace(/custom-/g, '')} days</u>`;
          } else {
                      // Default for monthly
            setuptext = `${fsetupfee} ${'for the <u>first month</u> then,'.locale()} `;
            text += `${fprice} ${'for each <u>month</u>'.locale()}`;
          }
      }
    }

    text += installmentText;

    if ((!trial || trial === 'None') && (setupfee > 0 || trial === 'Enabled')) { // Trial overwrites the setupfee
      text = setuptext + text; // Add setupfee text if no trial was set
    }

    text = `(${text})`; // Put long text in parenthesis
  } else {
    text += fprice; // Default price.
  }

  // Finish the sentence here
  // Place product name to first then, wrap details in span for easier styling
  return `${name || ''} <span class="form-product-details">${text}</span>`;
}

// build aria-labelledBy attribute for input field
// Returns a string with two ids:
// First one is top label id, the second one is sub label id
export function ariaLabelledBy(props, subLabelObject) {
  const topLabel = props?.id?.value && `label_${props.id.value}`;
  const subLabel = subLabelObject?.id && subLabelObject?.text && !props?.hiddenLabel && `sublabel_${subLabelObject.id}`;
  return [topLabel, subLabel].filter(Boolean).join(' ');
}

export function parseJSON(data) {
  try {
    if (!data) { return false; }
    if (typeof data === typeof 'string') {
      return JSON.parse(data);
    }
    return data;
  } catch (e) {
    return false;
  }
}

export function loadScript(file, callback = function () {}, arg) {
  try {
    const templateCache = {};
    templateCache[file] = callback;
    const script = global.document.createElement('script');
    script.type = 'text/javascript';
    script.src = `${file}?${(new Date()).getTime()}`;
    global.$(global.document.body).appendChild(script);
    if (arg === true) {
      callback();
    }
  } catch (e) {
    console.error(e);
  }
}

export function getDefaultDate(defaultDate, format, dateSeparator) {
  let liteModeInitialValue = '';
  let year = '';
  let month = '';
  let day = '';
  if (defaultDate) {
    if (defaultDate === 'none') {
      return '';
    } else if (defaultDate === 'current') {
      const date = new Date();
      month = addZeros(date.getMonth() + 1, 2);
      day = addZeros(date.getDate(), 2);
      year = date.getYear() < 1000 ? date.getYear() + 1900 : date.getYear();
    } else if (typeof defaultDate === 'string' && defaultDate.indexOf('custom_') > -1) {
      const dateString = defaultDate.split('_')[1];
      if (dateString) {
        const dateComponents = dateString.split('-');
        if (dateComponents.length > 2) {
          year = dateComponents[0] || '';
          month = dateComponents[1] || '';
          day = dateComponents[2] || '';
        }
      }
    }
    liteModeInitialValue = month + dateSeparator + day + dateSeparator + year;
    if (format === 'ddmmyyyy') {
      liteModeInitialValue = day + dateSeparator + month + dateSeparator + year;
    } else if (format === 'yyyymmdd') {
      liteModeInitialValue = year + dateSeparator + month + dateSeparator + day;
    }
  }
  return {
    litemode: liteModeInitialValue,
    year,
    month,
    day
  };
}

export function addZeros(num, totalDigits) {
  const n = num.toString();
  let pd = '';
  if (totalDigits > n.length) {
    for (let i = 0; i < (totalDigits - n.length); i++) {
      pd += '0';
    }
  }
  return pd + n.toString();
}


export function makeQuestionName(text) {
  let name = text.replace('&nbsp;', '');
  const tokens = name.split(/\s+/);
  name = fixUTF((tokens[1]) ? tokens[0].toLowerCase() + capitalize(tokens[1].toLowerCase()) : tokens[0].toLowerCase());
  name = name.replace(/\W/gim, '');
  return name.length === 0 ? 'input' : name;
}

const subLabelObj = (subLabelValue, key, id) => ({
  text: subLabelValue,
  id: id ? `${id}_${key}` : key
});

// Neil: id is for address sublabels - must have different element ID (89823)
export const subLabel = (prop, key, id) => (
  (prop.sublabels && prop.sublabels.value && (key in prop.sublabels.value))
    ? subLabelObj(prop.sublabels.value[key], key, id)
    : ''
);

export const generateChecker = (obj1, obj2) => {
  return (prop) => {
    const { value: v1 } = obj1[prop] || {};
    const { value: v2 } = obj2[prop] || {};

    if (typeof v1 === 'object') {
      return JSON.stringify(v1) !== JSON.stringify(v2);
    }

    return v1 !== v2;
  };
};
